import { Button, Group, Stack, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { useAdminImportTransactionType } from '@admin/hooks';
import { UploadFile } from '@components';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';

interface ImportTransactionTypeProps {
  onClose: () => void;
}

const importTransactionTypeSchema = z.object({
  file: z.instanceof(File),
});

type ImportTransactionTypeValues = z.infer<typeof importTransactionTypeSchema>;

export const ImportTransactionType: React.FC<ImportTransactionTypeProps> = ({ onClose }) => {
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const importTransactionTypeMutation = useAdminImportTransactionType();

  const form = useFormWithNameInputProp<ImportTransactionTypeValues>({
    initialValues: {
      file: null,
    },
    validate: zodResolver(importTransactionTypeSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: ImportTransactionTypeValues) => {
    setErrorMessage(null);
    setSubmitting(true);

    try {
      await importTransactionTypeMutation.mutateAsync(
        { file: values.file },
        {
          onSuccess: () => {
            onClose();
          },
        },
      );
    } catch (error) {
      setErrorMessage((error as any).response?.data?.message ?? 'An error occurred while importing the batch');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <UploadFile {...form.getInputProps('file')} isMulti={false} type="json" p="xs" />

        {errorMessage && (
          <Text c="red" fw="bold">
            {errorMessage}
          </Text>
        )}
      </Stack>
      <Group justify="center">
        <Button type="button" variant="transparent" onClick={onClose} disabled={submitting} size="lg">
          Cancel
        </Button>
        <Button type="submit" loading={submitting} disabled={!form.isValid()} variant="filled" size="lg">
          Import
        </Button>
      </Group>
    </form>
  );
};
